import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import {
  getRoutePath,
  loginRoutePath,
  forgotPasswordRoutePath,
  getLoginRoutePath,
} from '../app.routes';
import { AlertService } from '../common/alert/alert.service';
import { UserService } from '../common/user/user.service';

// Supabase will append #error={{type}}&error_code={{status}}&error_description={{description}} when there is an error confirming the users email address.
// Similarly, Supabase will append ?error={{type}}&error_description={{description}} when there is an error signing in with Apple.
// This guard will redirect the user to the appropriate page based on the error type (login for 401, forgot password for otp_expired).
export async function canActivateLayout(
  route: ActivatedRouteSnapshot
): Promise<boolean | UrlTree> {
  const alertService = inject(AlertService);
  const router = inject(Router);
  const userService = inject(UserService);

  if (route.fragment) {
    const params = new URLSearchParams(route.fragment);
    const errorCode = params.get('error_code');
    let message = params.get('error_description') || 'Unknown error';

    // Provide more user-friendly error messages for specific cases
    if (errorCode === 'otp_expired') {
      message =
        'Your password reset link has expired. Please request a new one.';
    }

    if (params.get('error')) {
      await alertService.createNotice({
        message,
        duration: 5000,
        position: 'bottom',
      });
    }

    // Redirect to appropriate page based on error type
    if (errorCode === '401') {
      return router.createUrlTree([getRoutePath(loginRoutePath)]);
    }

    if (errorCode === 'otp_expired') {
      return router.createUrlTree([getLoginRoutePath(forgotPasswordRoutePath)]);
    }
  }

  if (route.queryParamMap.has('error')) {
    const message =
      route.queryParamMap.get('error_description') || 'Unknown error';
    await alertService.createNotice({
      message,
      duration: 5000,
      position: 'bottom',
    });
  }

  const user = await userService.getUser();
  if (!user) {
    return router.createUrlTree([getRoutePath(loginRoutePath)]);
  }

  return true;
}
